import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { parseMarketplaceId, type MarketplaceId } from "@nova-ai/shared"
import { AlertTriangle, RefreshCw } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { z } from "zod"
import { AppShell } from "../components/layout/app-shell"
import { MarketplaceMark } from "../components/marketplaces/marketplace-mark"
import { Button } from "../components/ui/button"
import { Callout } from "../components/ui/callout"
import { Combobox } from "../components/ui/combobox"
import { RelativeTime } from "../components/ui/relative-time"
import { ScrollToTop } from "../components/ui/scroll-to-top"
import { SearchInput } from "../components/ui/search-input"
import { StatusBadge, type BadgeTone } from "../components/ui/status-badge"
import { OrdersList, OrdersTabs } from "../components/orders/orders-list"
import { SyncProgressPanel } from "../components/orders/orders-sync-panel"
import { useMarketplaces } from "../services/use-dashboard"
import { useRates } from "../services/use-rates"
import {
  useMagentoFreshness,
  useLatestOrdersSync,
  useOrderCounts,
  useOrders,
  type MagentoFreshness,
  type OrdersStateFilter,
} from "../services/use-orders"
import { isSyncing, useOrderSyncProgress, useStartOrderSync } from "../services/order-sync"
import { humanizeMarketplaceId } from "../lib/marketplace"
import { cn } from "../lib/utils"
import { formatDateParts, formatDateTime } from "../lib/format-date"

export const ordersSearch = z.object({
  state: z.enum(["all", "problem", "resolved"]).optional(),
  // Free-text id search (marketplace order id or Magento id).
  q: z.string().optional(),
  // Each entry is a marketplace id string, e.g. "decathlon-it". A single
  // string or an array round-trips through TanStack Router's JSON serializer.
  marketplace: z
    .union([z.string(), z.array(z.string())])
    .optional()
    .transform(toMarketplaceArray),
})

// TanStack Router's default JSON serializer round-trips an empty array as
// `?marketplace=%5B%5D`, and stale links can land here with that exact
// shape. Normalize aggressively so a "no marketplace selected" URL never
// turns into an API call that filters by the literal string `[]`.
function toMarketplaceArray(value: string | string[] | undefined): string[] {
  if (Array.isArray(value)) {
    return value.filter((v) => v.length > 0)
  }
  if (typeof value === "string" && value.length > 0 && value !== "[]") {
    return [value]
  }
  return []
}

export function safeParseMarketplaceId(id: string): MarketplaceId | null {
  try {
    return parseMarketplaceId(id)
  } catch {
    return null
  }
}

function useOrdersWorkspace() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: "/orders/" })
  const marketplaces = useMarketplaces()
  const state: OrdersStateFilter = search.state ?? "all"
  const selectedIds = search.marketplace
  const selectedMarketplaces = selectedIds
    .map(safeParseMarketplaceId)
    .filter((id): id is MarketplaceId => id !== null)
  const [query, setQuery] = useState(search.q ?? "")
  const pendingSearch = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cancelSearch = () => {
    if (pendingSearch.current !== null) {
      clearTimeout(pendingSearch.current)
    }
  }
  // Browser back/forward restores the input and cancels uncommitted keystrokes.
  useEffect(() => {
    setQuery(search.q ?? "")
    return () => {
      if (pendingSearch.current !== null) {
        clearTimeout(pendingSearch.current)
      }
    }
  }, [search.q])
  function changeQuery(next: string) {
    setQuery(next)
    cancelSearch()
    pendingSearch.current = setTimeout(() => {
      void navigate({ search: (previous) => ({ ...previous, q: next.trim() || undefined }) })
    }, 300)
  }
  function clearFilters() {
    cancelSearch()
    setQuery("")
    void navigate({ search: (previous) => ({ ...previous, marketplace: undefined, q: undefined }) })
  }
  const freshness = useMagentoFreshness()
  const latestSync = useLatestOrdersSync()
  const syncVersion = latestSync.data?.lastSyncedAt
  const orders = useOrders({
    marketplaces: selectedMarketplaces,
    state,
    search: search.q ?? "",
    syncVersion,
  })
  const counts = useOrderCounts(selectedMarketplaces, syncVersion)
  const rates = useRates()
  const rows = (orders.data?.pages ?? []).flatMap((page) => page.orders)
  const progress = useOrderSyncProgress()
  const startSync = useStartOrderSync()
  const hasFilters = selectedIds.length > 0 || query.length > 0 || Boolean(search.q)
  const options = (marketplaces.data ?? []).map((id) => ({
    value: id,
    label: humanizeMarketplaceId(id),
    icon: <MarketplaceMark marketplace={id} />,
  }))

  return {
    search,
    navigate,
    state,
    selectedIds,
    query,
    changeQuery,
    clearFilters,
    freshness,
    latestSync,
    orders,
    counts,
    rates,
    rows,
    progress,
    startSync,
    hasFilters,
    options,
  }
}

function OrdersGrid() {
  const {
    search,
    navigate,
    state,
    selectedIds,
    query,
    changeQuery,
    clearFilters,
    freshness,
    latestSync,
    orders,
    counts,
    rates,
    rows,
    progress,
    startSync,
    hasFilters,
    options,
  } = useOrdersWorkspace()
  return (
    <div
      className={cn(
        "mx-auto max-w-[1160px] [&_h1]:sm:text-[28px]",
        "[:root:has(&)]:[scrollbar-gutter:stable]",
        "[&_.text-warning]:text-[color:color-mix(in_oklab,var(--color-warning)_75%,var(--color-foreground))]"
      )}
    >
      <AppShell
        title="Orders"
        fullWidth
        description="Follow each marketplace order through Baselinker, Magento and OMS, and fix the ones that get stuck."
        actions={
          <div className="flex w-full items-center justify-between gap-3 sm:w-auto sm:min-w-max">
            <LastOrdersSyncLabel lastSyncedAt={latestSync.data?.lastSyncedAt} />

            <Button
              variant="secondary"
              className="min-h-11 shrink-0"
              onClick={startSync}
              disabled={isSyncing(progress)}
            >
              <RefreshCw
                className={cn(
                  "size-4",
                  isSyncing(progress) && "animate-spin motion-reduce:animate-none"
                )}
                aria-hidden
              />

              {isSyncing(progress) ? "Syncing…" : "Sync orders"}
            </Button>
          </div>
        }
      >
        <MagentoFreshnessBanner freshness={freshness.data} />

        {progress ? <SyncProgressPanel progress={progress} /> : null}

        <OrdersTabs
          state={state}
          counts={counts.isError ? undefined : counts.data}
          onChange={(next) =>
            void navigate({
              search: (previous) => ({ ...previous, state: next === "all" ? undefined : next }),
            })
          }
        />

        {counts.isError ? (
          <p role="status" className="mb-3 text-xs text-muted-foreground">
            Order counts are unavailable.
          </p>
        ) : null}

        <section
          id="orders-panel"
          role="tabpanel"
          aria-labelledby={`orders-tab-${state}`}
          className="min-w-0 rounded-xl border border-border bg-card"
        >
          <div className="flex flex-col gap-3 border-b border-border p-4 sm:flex-row sm:flex-wrap sm:items-center">
            <Combobox
              multiple
              value={selectedIds}
              options={options}
              onChange={(next) =>
                void navigate({
                  search: (previous) => ({
                    ...previous,
                    marketplace: next.length ? next : undefined,
                  }),
                })
              }
              placeholder="All marketplaces"
              aria-label="Marketplaces"
              searchPlaceholder="Search marketplaces…"
              clearLabel="All marketplaces"
              emptyMessage="No marketplaces"
              selectionLabel="marketplaces"
              touchFriendly
              className="w-full sm:w-56"
            />

            <SearchInput
              value={query}
              onChange={changeQuery}
              placeholder="Order id or Magento id"
              ariaLabel="Order id or Magento id"
              className="w-full sm:w-64 [&_input]:h-11 [&_input]:pr-12 [&_input]:text-base [&_input]:placeholder:text-muted-foreground [&_button]:right-0 [&_button]:size-11"
            />

            {!orders.isLoading && !orders.isError ? (
              <span className="text-xs text-muted-foreground tabular-nums sm:ml-auto">
                {orders.hasNextPage
                  ? `${rows.length} loaded`
                  : `${rows.length} ${rows.length === 1 ? "order" : "orders"}`}
              </span>
            ) : null}

            {hasFilters ? (
              <Button variant="ghost" className="min-h-11" onClick={clearFilters}>
                Clear filters
              </Button>
            ) : null}
          </div>

          <OrdersList
            rows={rows}
            rates={rates.data}
            state={state}
            search={{
              state: search.state,
              q: search.q,
              marketplace: selectedIds.length ? selectedIds : undefined,
            }}
            loading={orders.isLoading}
            error={orders.error}
            hasNextPage={orders.hasNextPage}
            fetchingNextPage={orders.isFetchingNextPage}
            onLoadMore={() => void orders.fetchNextPage()}
            onRetry={() => void orders.refetch()}
            clearFilters={hasFilters ? clearFilters : undefined}
          />
        </section>

        <ScrollToTop className="bottom-24 size-11" />
      </AppShell>
    </div>
  )
}

function LastOrdersSyncLabel({ lastSyncedAt }: { lastSyncedAt: string | null | undefined }) {
  if (lastSyncedAt === undefined) {
    return null
  }
  if (!lastSyncedAt) {
    return <span className="text-xs text-muted-foreground">Not synced yet</span>
  }
  return (
    <span
      className="whitespace-nowrap text-xs text-muted-foreground tabular-nums"
      title={`Last synced ${formatDateTime(lastSyncedAt)}`}
    >
      Synced <RelativeTime iso={lastSyncedAt} />
    </span>
  )
}

function MagentoFreshnessBanner({ freshness }: { freshness: MagentoFreshness | undefined }) {
  if (!freshness?.stale) {
    return null
  }
  const age = freshness.ageSeconds === null ? null : formatAge(freshness.ageSeconds)
  const time = freshness.latestOrderCreatedAt
    ? formatDateParts(freshness.latestOrderCreatedAt).time
    : null
  return (
    <Callout tone="warning" dense className="mb-6 flex items-start gap-2 text-sm">
      <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />

      <span>
        <strong className="mr-1 font-medium">
          {age ? `Magento is about ${age} behind.` : "Magento data is stale."}
        </strong>

        <span className="block text-muted-foreground sm:inline">
          Orders created {time ? `after ${time}` : "recently"} can show “Not found” until it catches
          up.
        </span>
      </span>
    </Callout>
  )
}

function formatAge(seconds: number): string {
  if (seconds < 90 * 60) {
    return `${Math.round(seconds / 60)} min`
  }
  const hours = seconds / 3600
  return hours < 48 ? `${hours.toFixed(1)} h` : `${Math.round(hours / 24)} d`
}

const STATE_CHIP_TONES: Record<"good" | "warn" | "bad" | "info", BadgeTone> = {
  good: "success",
  warn: "warning",
  bad: "destructive",
  info: "info",
}

export function StateChip({
  kind,
  children,
}: {
  kind: "good" | "warn" | "bad" | "info"
  children: React.ReactNode
}) {
  return <StatusBadge tone={STATE_CHIP_TONES[kind]}>{children}</StatusBadge>
}

export const Route = createFileRoute("/orders/")({
  validateSearch: ordersSearch,
  component: OrdersGrid,
})
